import { fill, t, type StringKey } from '../strings';
import { ADDRESS_SOURCE_NAME_KEYS, type Address } from './addressVerification';

/**
 * A low-confidence address answer, kept and shown (piece 21; decision 7430,
 * amending 7320). A service that answers below its bar never counts toward
 * Verified or Corrected, but what it said is not thrown away: the person sees
 * the service, how sure it was, the address it placed, and one sentence on why
 * it is unsure and what to check.
 */
export const ADDRESS_NOTE_REASONS = ['numberEstimated', 'postalCodeDiffers', 'cityDiffers', 'streetNotHeld', 'areaOnly'] as const;
export type AddressNoteReason = (typeof ADDRESS_NOTE_REASONS)[number];

export interface AddressConfidenceNote {
  /** The provider key of the service that answered: `geoapify`, `google`. */
  service: string;
  /** The service's confidence as a whole percent; null where the service gives none. */
  confidence: number | null;
  /** The one-line address the service placed; null on an Outside Calls row, which keeps no address (decision 5880). */
  address: string | null;
  reason: AddressNoteReason;
  /** The part as typed and as placed, for a reason that names a part; absent on an Outside Calls row, which keeps neither. */
  typed?: string | null;
  found?: string | null;
  /**
   * 🔒 True where this service confirms the street and the postal code and only estimates the house number, which earns the answer
   * Estimated when no service confirms the building (decision 7460). Absent on an Outside Calls row.
   */
  streetConfirmed?: boolean;
  /**
   * 🔒 The address the service placed, in parts, where it differs from the one typed and holds a street line: what Use This Address
   * opens Edit Location filled with (decision 7460). Absent on an Outside Calls row, which keeps no address.
   */
  placed?: Address;
}

/** Whether a set of notes holds a service that confirms the street and the postal code and estimates the number (decision 7460). */
export function notesEstimate(notes: readonly AddressConfidenceNote[] | null | undefined): boolean {
  return (notes ?? []).some((note) => note.streetConfirmed === true);
}

export function isAddressConfidenceNote(value: unknown): value is AddressConfidenceNote {
  if (typeof value !== 'object' || value === null) return false;
  const note = value as Record<string, unknown>;
  return typeof note.service === 'string' && (note.confidence === null || typeof note.confidence === 'number') && (note.address === null || typeof note.address === 'string') && (ADDRESS_NOTE_REASONS as readonly unknown[]).includes(note.reason);
}

const SENTENCE_KEYS: Readonly<Record<AddressNoteReason, StringKey>> = {
  numberEstimated: 'address.note.numberEstimated',
  postalCodeDiffers: 'address.note.postalCodeDiffers',
  cityDiffers: 'address.note.cityDiffers',
  streetNotHeld: 'address.note.streetNotHeld',
  areaOnly: 'address.note.areaOnly',
};

const UNNAMED_KEYS: Readonly<Partial<Record<AddressNoteReason, StringKey>>> = {
  postalCodeDiffers: 'address.note.postalCodeDiffersUnnamed',
  cityDiffers: 'address.note.cityDiffersUnnamed',
};

/** The service's name as a line opens on it: *Geoapify*, *Google*, *the Census Bureau* capitalised. A key the page does not know reads as itself. */
export function noteServiceName(service: string): string {
  const name = service in ADDRESS_SOURCE_NAME_KEYS ? t(ADDRESS_SOURCE_NAME_KEYS[service as keyof typeof ADDRESS_SOURCE_NAME_KEYS]) : service;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** The one sentence on why the answer is unsure and what to check; a part differing names both values where the note carries them. */
export function noteSentence(note: Pick<AddressConfidenceNote, 'reason' | 'typed' | 'found'>): string {
  const named = note.typed && note.found;
  const key = named ? SENTENCE_KEYS[note.reason] : (UNNAMED_KEYS[note.reason] ?? SENTENCE_KEYS[note.reason]);
  return fill(t(key), { typed: note.typed ?? '', found: note.found ?? '' });
}

/** *Geoapify · 50% · 100 Pattons Run Road, Martins Ferry, OH 43935*, each part left off where the note holds none. */
export function noteLine(note: Pick<AddressConfidenceNote, 'service' | 'confidence' | 'address'>): string {
  return [noteServiceName(note.service), note.confidence === null ? null : `${note.confidence}%`, note.address].filter((part): part is string => Boolean(part)).join(' · ');
}

/** A confidence between 0 and 1 as a whole percent; null where the service gave none. */
export function wholePercent(confidence: number | null | undefined): number | null {
  return typeof confidence === 'number' && Number.isFinite(confidence) ? Math.round(Math.min(1, Math.max(0, confidence)) * 100) : null;
}
